import React from "react";
import GenericTable from "./GenericTable";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ChatRoundedIcon from "@mui/icons-material/ChatRounded";
import { IconButton, Tooltip, Box, Typography } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ClientBulkActions from "./clients/ClientBulkActions";
import ClientRelationshipBadge from "./Chat/ClientRelationshipBadge";
import { getClientCommercialName } from "../utils/clientName";
import { getClientListRelationshipProps } from "../utils/clientRelationships";

const ClientTable = ({
  clients = [],
  rowCount,
  pageSize,
  page,
  onPageChange,
  onPageSizeChange,
  onSearch,
  onEditClient,
  onDeleteClient,
  onStartConversation,
  isLoading,

  onDeleteSelected,
  onMergeSelected,
  onSelectionChange,
  onReviewSelected,
  onOpenFilters,
  activeFilterCount = 0,
  hasActiveFilters = false,
  onReviewFilter,
  onPortalInviteSelected,
  portalInviteDisabled = false,
  portalInviteDisabledReason = "",
  onCreateCampaignSelected,
  campaignCreateEnabled = false,
  canManageClients = true,
}) => {
  const { t } = useTranslation("settings");
  const { t: tc } = useTranslation("common");
  const navigate = useNavigate();
  const rows = clients.map((client) => {
    const displayName = getClientCommercialName(client) || "N/A";

    return {
      id: client.id,
      nome: displayName,
      email: client.email || t("shared.notApplicable"),
      cidade: client.cidade || t("shared.notApplicable"),
      uf: client.uf || t("shared.notApplicable"),
      _raw: client,
    };
  });

  const columns = [
    { field: "id", headerName: "ID", width: 100 },
    {
      field: "nome",
      headerName: t("clientTable.colName"),
      minWidth: 260,
      flex: 1,
      renderCell: (params) => {
        const raw = params?.row?._raw || params?.row || {};
        const { count, hint } = getClientListRelationshipProps(raw);
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              minWidth: 0,
              width: "100%",
            }}
          >
            <Typography
              noWrap
              variant="body2"
              sx={{ minWidth: 0, flex: "0 1 auto" }}
            >
              {params.value}
            </Typography>
            {count > 0 ? (
              <ClientRelationshipBadge count={count} hint={hint} compact />
            ) : null}
          </Box>
        );
      },
    },
    { field: "email", headerName: t("shared.email"), width: 300 },
    { field: "cidade", headerName: t("clientTable.colCity"), width: 150 },
    { field: "uf", headerName: t("clientTable.colState"), width: 120 },
    {
      field: "actions",
      headerName: t("clientTable.colActions"),
      width: 250,
      renderCell: (params) => {
        const clientPayload =
          params?.row?._raw ||
          {
            id: params?.row?.id ?? params?.id,
            nome: params?.row?.nome,
            email: params?.row?.email,
            cidade: params?.row?.cidade,
            uf: params?.row?.uf,
          };

        return (
          <Box display="flex" gap={1}>
            <Tooltip title={t("shared.startConversation")}>
              <IconButton
                size="small"
                onClick={() => onStartConversation && onStartConversation(clientPayload)}
              >
                <ChatRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title={t("shared.viewDetails")}>
              <IconButton size="small" component={Link} to={`/clients/${params.id}`}>
                <InfoRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {canManageClients ? (
              <Tooltip title={t("shared.edit")}>
                <IconButton
                  size="small"
                  onClick={() =>
                    onEditClient && onEditClient(params?.row?.id ?? params?.id ?? params?.row)
                  }
                >
                  <EditRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}

            {canManageClients ? (
              <Tooltip title={t("shared.delete")}>
                <IconButton
                  size="small"
                  onClick={() => onDeleteClient && onDeleteClient(params.row.id)}
                >
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>
        );
      },
    },
  ];

  return (
    <GenericTable
      rows={rows}
      columns={columns}
      rowCount={rowCount}
      pageSize={pageSize}
      page={page}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      onSearch={onSearch}
      isLoading={isLoading}
      onExportPDFTitle={t("clientTable.exportTitle")}
      exportFileName="clientes"
      showExport={canManageClients}
      checkboxSelection={canManageClients}
      onDeleteSelected={canManageClients ? onDeleteSelected : undefined}
      onSelectionChange={onSelectionChange}
      onOpenAdvancedFilters={onOpenFilters}
      advancedFiltersActiveCount={activeFilterCount}
      bulkActions={
        canManageClients
          ? (ctx) => (
              <ClientBulkActions
                {...ctx}
                hasActiveFilters={hasActiveFilters}
                onReviewSelected={onReviewSelected}
                onReviewFilter={onReviewFilter}
                onMergeSelected={onMergeSelected}
                onPortalInviteSelected={onPortalInviteSelected}
                portalInviteDisabled={portalInviteDisabled}
                portalInviteDisabledReason={portalInviteDisabledReason}
                onCreateCampaignSelected={onCreateCampaignSelected}
                campaignCreateEnabled={campaignCreateEnabled}
              />
            )
          : undefined
      }
      deleteSelectedLabel={tc("genericTable.deleteSelected")}
      deleteConfirmTitle={t("shared.deleteSelectedTitle")}
      deleteConfirmMessage={(count) => t("shared.deleteSelectedConfirm", { count })}
      onRowClick={(row) => {
        const id = row?.id ?? row?._raw?.id;
        if (id) navigate(`/clients/${id}`);
      }}
    />
  );
};

export default ClientTable;
